// Aufträge & Tickets, Rechnungen, Bewertung im Dienstleister-Bereich (Handoff §9, E6). Reine Funktionen.

import { istOffeneRechnung } from './liste';
import { letzteMonate } from './liste';
import { TICKET_STUFEN, ticketStufe, type TicketStufe } from './detail';

// ── Aufträge ───────────────────────────────────────────────────────────────────

const REIHE: readonly TicketStufe[] = ['neu', 'beauftragt', 'in_arbeit', 'erledigt', 'abgerechnet'];
export function naechsteStufe(s: TicketStufe): TicketStufe | null {
  const i = REIHE.indexOf(s);
  return i >= 0 && i < REIHE.length - 1 ? REIHE[i + 1]! : null;
}

/** Stufe → Ticket-Status (Entscheidung 14.10.2026); Abschluss mit Grund wie im Abschluss-Dialog. */
export function statusFuerStufe(s: TicketStufe, beauftragtAm: string | null, jetzt: string): { status: string; grund?: string; zusatz?: Record<string, string> } {
  switch (s) {
    case 'beauftragt': return { status: 'warten_auf_dienstleister', zusatz: beauftragtAm ? {} : { beauftragt_am: jetzt } };
    case 'in_arbeit': return { status: 'in_bearbeitung' };
    case 'erledigt': return { status: 'erledigt', grund: 'Durch Dienstleister erledigt' };
    case 'abgerechnet': return { status: 'geschlossen', grund: 'abgerechnet' };
    default: return { status: 'neu' };
  }
}

export type TicketFilter = 'offen' | 'erledigt' | 'alle';
export function passtTicketFilter(status: string | null, f: TicketFilter): boolean {
  const s = ticketStufe(status);
  if (f === 'alle') return true;
  const offen = s === 'neu' || s === 'beauftragt' || s === 'in_arbeit';
  return f === 'offen' ? offen : !offen;
}

export function stufenZaehler(tickets: readonly { status: string | null }[]): Record<TicketStufe, number> {
  const z = Object.fromEntries(TICKET_STUFEN.map(s => [s.key, 0])) as Record<TicketStufe, number>;
  for (const t of tickets) z[ticketStufe(t.status)] += 1;
  return z;
}

export interface HistorieEintrag { ticket_id: string; aktion: string; neuer_wert: string | null; created_at: string }

/**
 * Ø Reaktionszeit in Stunden: von der Beauftragung (beauftragt_am) bis zum ersten Statuswechsel
 * auf „in Bearbeitung“ oder „erledigt“. Ohne messbare Fälle null.
 */
export function reaktionsStunden(tickets: readonly { id: string; beauftragt_am: string | null }[], historie: readonly HistorieEintrag[]): number | null {
  const dauern: number[] = [];
  for (const t of tickets) {
    if (!t.beauftragt_am) continue;
    const start = Date.parse(t.beauftragt_am);
    const reaktion = historie
      .filter(h => h.ticket_id === t.id && h.aktion === 'status_change' && (h.neuer_wert === 'in_bearbeitung' || h.neuer_wert === 'erledigt') && Date.parse(h.created_at) >= start)
      .map(h => Date.parse(h.created_at)).sort((a, b) => a - b)[0];
    if (reaktion !== undefined) dauern.push((reaktion - start) / 3_600_000);
  }
  return dauern.length ? Math.round((dauern.reduce((s, d) => s + d, 0) / dauern.length) * 10) / 10 : null;
}

/** Kostenrahmen über der Freigabegrenze → Freigabe durch Eigentümer bzw. WEG-Beschluss nötig. */
export function freigabeNoetig(kostenrahmen: number | null, grenze: number): boolean {
  return kostenrahmen !== null && Number.isFinite(kostenrahmen) && kostenrahmen > grenze;
}

/** Erledigte Aufträge je Monat (12 Monate) – nach letzter Änderung des erledigten Tickets. */
export function auftraegeJeMonat(tickets: readonly { status: string | null; updated_at: string | null; created_at: string }[], heute: string): { monat: string; anzahl: number }[] {
  return letzteMonate(heute).map(m => ({
    monat: m,
    anzahl: tickets.filter(t => { const s = ticketStufe(t.status); return (s === 'erledigt' || s === 'abgerechnet') && (t.updated_at ?? t.created_at).startsWith(m); }).length,
  }));
}

// ── Rechnungen ─────────────────────────────────────────────────────────────────

export type RechnungStufe = 'offen' | 'geprueft' | 'bezahlt';
export function rechnungStufe(r: { status: string | null; technisch_geprueft_am: string | null }): RechnungStufe {
  if (!istOffeneRechnung(r.status)) return 'bezahlt';
  return r.technisch_geprueft_am ? 'geprueft' : 'offen';
}
export const RECHNUNG_STUFE: Record<RechnungStufe, { label: string; ton: 'warn' | 'info' | 'ok'; aktion: string | null }> = {
  offen: { label: 'offen', ton: 'warn', aktion: 'sachlich prüfen' },
  geprueft: { label: 'geprüft', ton: 'info', aktion: 'zur Zahlung freigeben' },
  bezahlt: { label: 'bezahlt', ton: 'ok', aktion: null },
};

export function rechnungKennzahlen(rechnungen: readonly { brutto: number | null; status: string | null; datum: string | null; bezahlt_timestamp: string | null; lohnanteil_35a: number | null }[], jahr: string) {
  const offen = rechnungen.filter(r => istOffeneRechnung(r.status)).reduce((s, r) => s + (r.brutto ?? 0), 0);
  const bezahltJahr = rechnungen.filter(r => !istOffeneRechnung(r.status) && (r.bezahlt_timestamp ?? r.datum ?? '').startsWith(jahr)).reduce((s, r) => s + (r.brutto ?? 0), 0);
  const volumen = rechnungen.filter(r => (r.datum ?? '').startsWith(jahr)).reduce((s, r) => s + (r.brutto ?? 0), 0);
  const mitLohn = rechnungen.filter(r => r.lohnanteil_35a !== null && (r.datum ?? '').startsWith(jahr));
  return { offen, bezahltJahr, volumen, lohnanteil: mitLohn.length ? mitLohn.reduce((s, r) => s + (r.lohnanteil_35a ?? 0), 0) : null };
}

export interface RechnungEntwurf { nummer: string; betrag: string; datum: string; leistung: string; umlagefaehig: boolean; reverseCharge: boolean }

export function rechnungFehler(e: RechnungEntwurf, betrag: number | null): string | null {
  if (!e.nummer.trim()) return 'Rechnungsnummer fehlt';
  if (betrag === null || Number.isNaN(betrag) || betrag <= 0) return 'Betrag (brutto) angeben';
  if (!e.datum) return 'Rechnungsdatum fehlt';
  return null;
}

// ── Bewertung ──────────────────────────────────────────────────────────────────

export const KRITERIEN: readonly { key: string; label: string }[] = [
  { key: 'qualitaet', label: 'Qualität' }, { key: 'termintreue', label: 'Termintreue' }, { key: 'preis', label: 'Preis-Leistung' },
  { key: 'kommunikation', label: 'Kommunikation' }, { key: 'sauberkeit', label: 'Sauberkeit' },
];

/** Schnitt je Kriterium aus den erfassten Bewertungen (fehlend = null). */
export function kriterienSchnitt(bewertungen: readonly { kriterien: Record<string, number> | null }[]): Record<string, number | null> {
  const aus: Record<string, number | null> = {};
  for (const k of KRITERIEN) {
    const werte = bewertungen.map(b => b.kriterien?.[k.key]).filter((v): v is number => typeof v === 'number' && v >= 1 && v <= 5);
    aus[k.key] = werte.length ? Math.round((werte.reduce((s, v) => s + v, 0) / werte.length) * 10) / 10 : null;
  }
  return aus;
}

/** Steuersatz: 0 % bei Kleinunternehmer (§ 19) oder § 13b (Steuer schuldet der Empfänger), sonst 19 %. */
export function steuerAufteilung(brutto: number, ohneUst: boolean): { netto: number; steuer: number; steuersatz: number } {
  if (ohneUst) return { netto: brutto, steuer: 0, steuersatz: 0 };
  const netto = Math.round((brutto / 1.19) * 100) / 100;
  return { netto, steuer: Math.round((brutto - netto) * 100) / 100, steuersatz: 19 };
}

export function rechnungPayload(e: RechnungEntwurf, betrag: number, dl: { id: string; name: string; iban: string | null; kontoinhaber: string | null; kleinunternehmer: boolean }): Record<string, string | number | boolean | null> {
  const st = steuerAufteilung(betrag, e.reverseCharge || dl.kleinunternehmer);
  return {
    rechnungsnummer: e.nummer.trim(), unternehmen: dl.name, bezeichnung: e.leistung.trim() || 'Rechnung', brutto: betrag, netto: st.netto, steuer: st.steuer,
    steuersatz: st.steuersatz, waehrung: 'EUR', datum: e.datum, status: 'offen', richtung: 'eingang', quelle: 'manuell', dienstleister_id: dl.id,
    iban: dl.iban, zahlungsempfaenger: dl.kontoinhaber || dl.name, umlagefaehig: e.umlagefaehig, reverse_charge_13b: e.reverseCharge,
  };
}
